/** The module's string operators (`Audience/form/components/Personal.tsx`). */
export type AudienceOperator = 'eq' | 'neq' | 'in' | 'not_in';

export const AUDIENCE_OPERATORS: AudienceOperator[] = ['eq', 'neq', 'in', 'not_in'];

/** One custom property card; a locked one comes from Orbit's Layer 2 signal and is read-only. */
export interface AudienceProperty {
  id: number;
  key: string;
  operator: AudienceOperator;
  value: string;
  locked: boolean;
}

/** The attribute tiles of 68, as the module's filter keys (`AudienceFilters.tsx`). */
export type AudienceAttribute =
  | 'age'
  | 'birthmonth'
  | 'gender'
  | 'race'
  | 'location'
  | 'earned_badges'
  | 'campaign_enrolment'
  | 'leaderboard_position'
  | 'loyalty_points_balance'
  | 'loyalty_tier'
  | 'account_confirmed_at'
  | 'earned_rewards'
  | 'custom';

export const ATTRIBUTE_GROUPS: {
  group: 'demographics' | 'engagement' | 'custom';
  attributes: AudienceAttribute[];
}[] = [
  { group: 'demographics', attributes: ['age', 'birthmonth', 'gender', 'race', 'location'] },
  {
    group: 'engagement',
    attributes: [
      'earned_badges',
      'campaign_enrolment',
      'leaderboard_position',
      'loyalty_points_balance',
      'loyalty_tier',
      'account_confirmed_at',
      'earned_rewards'
    ]
  },
  { group: 'custom', attributes: ['custom'] }
];

export interface AudienceState {
  name: string;
  attributes: AudienceAttribute[];
  properties: AudienceProperty[];
}

let nextId = 1;

export const newProperty = (): AudienceProperty => {
  nextId += 1;
  return { id: nextId, key: '', operator: 'eq', value: '', locked: false };
};

export const initialAudienceState = (): AudienceState => ({
  name: '',
  attributes: [],
  properties: []
});

/** Picking "Custom" opens one property card; un-picking it keeps only the locked ones. */
export const toggleAttribute = (
  state: AudienceState,
  attribute: AudienceAttribute
): AudienceState => {
  const on = state.attributes.includes(attribute);
  const attributes = on
    ? state.attributes.filter(item => item !== attribute)
    : [...state.attributes, attribute];
  if (attribute !== 'custom') {
    return { ...state, attributes };
  }
  const properties = on
    ? state.properties.filter(property => property.locked)
    : state.properties.some(property => !property.locked)
    ? state.properties
    : [...state.properties, newProperty()];
  return { ...state, attributes, properties };
};
